const plugins = new Map();

const indexes = {
	destinations: new Map(),
	sections: new Map(),
	resolvers: new Map(),
	filters: new Map(),
	focusHandlers: new Map(),
	modals: new Map(),
	experiences: new Map(),
	prefillMappers: new Map(),
	statistics: new Map(),
	datasets: new Map(),
	completionVerifiers: new Map(),
	contextSerializers: new Map(),
	operationGuards: new Map(),
	capabilities: new Map(),
};

const INDEX_KEYS = Object.keys(indexes);
const SUPPORTED_COMMAND_VERSION = 1;

/**
 * Normalize a plugin mapping to entries.
 * @param {object|null} value plugin mapping
 * @return {Array} mapping entries
 */
function entries(value) {
	return Object.entries(value || {});
}

/**
 * Validate the stable plugin envelope.
 * @param {object} plugin resource plugin
 * @return {void}
 */
function assertPlugin(plugin) {
	if (!plugin || typeof plugin !== 'object') {
		throw new Error('invalid_resource_plugin');
	}
	if (typeof plugin.id !== 'string' || !plugin.id.trim()) {
		throw new Error('resource_plugin_id_required');
	}
	if (!Number.isInteger(plugin.version) || plugin.version < 1) {
		throw new Error('resource_plugin_version_required');
	}
	if (
		plugin.resource != null &&
		(typeof plugin.resource !== 'string' || !plugin.resource.trim())
	) {
		throw new Error('invalid_resource_plugin_resource');
	}
	if (plugin.operations != null && !Array.isArray(plugin.operations)) {
		throw new Error('invalid_resource_plugin_operations');
	}
	if (plugin.dependencies != null && typeof plugin.dependencies !== 'object') {
		throw new Error('invalid_resource_plugin_dependencies');
	}
	INDEX_KEYS.forEach((key) => {
		if (plugin[key] != null && typeof plugin[key] !== 'object') {
			throw new Error(`invalid_resource_plugin_${key}`);
		}
	});
}

/**
 * Add derived capability identifiers to a resource plugin.
 * @param {object} plugin resource plugin
 * @return {object} normalized plugin
 */
function normalizePlugin(plugin) {
	const derivedCapabilities = plugin.resource
		? Object.fromEntries(
			(plugin.operations || []).map((operation) => [
				`${plugin.resource}.${operation}`,
				true,
			]),
		)
		: {};
	return {
		...plugin,
		capabilities: {
			...derivedCapabilities,
			...(plugin.capabilities || {}),
		},
	};
}

/**
 * Add one plugin to every semantic index.
 * @param {object} plugin resource plugin
 * @return {void}
 */
function indexPlugin(plugin) {
	INDEX_KEYS.forEach((key) => {
		entries(plugin[key]).forEach(([semanticId, value]) => {
			const current = indexes[key].get(semanticId);
			if (current && current.pluginId !== plugin.id) {
				throw new Error(`duplicate_${key}_${semanticId}`);
			}
			indexes[key].set(semanticId, {pluginId: plugin.id, value});
		});
	});
}

/**
 * Remove index entries owned by a plugin.
 * @param {string} pluginId plugin identifier
 * @return {void}
 */
function removePluginFromIndexes(pluginId) {
	INDEX_KEYS.forEach((key) => {
		indexes[key].forEach((entry, semanticId) => {
			if (entry.pluginId === pluginId) indexes[key].delete(semanticId);
		});
	});
}

/**
 * Register or replace one resource plugin.
 * @param {object} plugin resource plugin
 * @return {Function} unregister callback
 */
export function registerResourcePlugin(plugin) {
	assertPlugin(plugin);
	const normalized = normalizePlugin(plugin);
	const existing = plugins.get(normalized.id);
	if (existing) removePluginFromIndexes(normalized.id);
	try {
		indexPlugin(normalized);
		plugins.set(normalized.id, normalized);
	} catch (error) {
		removePluginFromIndexes(normalized.id);
		if (existing) {
			indexPlugin(existing);
			plugins.set(existing.id, existing);
		}
		throw error;
	}
	return () => {
		if (plugins.get(normalized.id) !== normalized) return;
		removePluginFromIndexes(normalized.id);
		plugins.delete(normalized.id);
	};
}

/**
 * Return one plugin dependency list.
 * @param {object} plugin resource plugin
 * @param {string} dependencyType dependency category
 * @return {Array<string>} dependency identifiers
 */
function dependencyValues(plugin, dependencyType) {
	const values = plugin.dependencies?.[dependencyType];
	return Array.isArray(values) ? values : [];
}

/**
 * Validate dependencies after all discovered plugins are registered.
 * @return {void}
 */
export function validateResourcePlugins() {
	plugins.forEach((plugin) => {
		if ((plugin.commandVersion || 1) !== SUPPORTED_COMMAND_VERSION) {
			throw new Error(`unsupported_command_version_${plugin.id}`);
		}
		dependencyValues(plugin, 'plugins').forEach((pluginId) => {
			if (!plugins.has(pluginId)) {
				throw new Error(`missing_plugin_dependency_${plugin.id}_${pluginId}`);
			}
		});
		[
			['resolvers', 'resolvers'],
			['destinations', 'destinations'],
			['sections', 'sections'],
			['modals', 'modals'],
		].forEach(([dependencyType, indexName]) => {
			dependencyValues(plugin, dependencyType).forEach((semanticId) => {
				if (!indexes[indexName].has(semanticId)) {
					throw new Error(
						`missing_${dependencyType}_dependency_${plugin.id}_${semanticId}`,
					);
				}
			});
		});
	});
}

/**
 * Resolve a semantic id from one index.
 * @param {string} index index name
 * @param {string} semanticId semantic identifier
 * @return {*|null} registered value
 */
function resolve(index, semanticId) {
	return indexes[index].get(semanticId)?.value || null;
}

/**
 * Resolve a destination descriptor.
 * @param {string} destinationId destination identifier
 * @return {object|null} destination descriptor
 */
export function getDestination(destinationId) {
	return resolve('destinations', destinationId);
}

/**
 * Resolve a section handler.
 * @param {string} sectionId section identifier
 * @return {Function|null} section handler
 */
export function getSectionHandler(sectionId) {
	return resolve('sections', sectionId);
}

/**
 * Resolve an entity resolver.
 * @param {string} entityType entity type
 * @return {Function|null} resolver
 */
export function getResolver(entityType) {
	return resolve('resolvers', entityType);
}

/**
 * Resolve a filter handler.
 * @param {string} resource resource identifier
 * @return {Function|null} filter handler
 */
export function getFilterHandler(resource) {
	return resolve('filters', resource);
}

/**
 * Resolve a handler that reads a figure Canvas already keeps.
 * @param {string} metric statistic identifier
 * @return {Function|null} statistic handler
 */
export function getStatisticHandler(metric) {
	return resolve('statistics', metric);
}

/**
 * Resolve a dataset reader.
 *
 * Registered by a plugin rather than imported by the provider, the way every
 * other API-touching handler here is. The provider stays free of the API
 * client that way - it holds the command wiring, and the modules that
 * actually call Canvas are pulled in by whoever registers them.
 *
 * @param {string} sourceId source the agent named
 * @return {Function|null} dataset reader
 */
export function getDatasetHandler(sourceId) {
	return resolve('datasets', sourceId);
}

/**
 * Resolve an entity-focus handler.
 * @param {string} resource resource identifier
 * @return {Function|null} focus handler
 */
export function getFocusHandler(resource) {
	return resolve('focusHandlers', resource);
}

/**
 * Resolve a modal handler.
 * @param {string} modalId modal identifier
 * @return {Function|null} modal handler
 */
export function getModalHandler(modalId) {
	return resolve('modals', modalId);
}

/**
 * Resolve an experience handler.
 * @param {string} experienceId experience identifier
 * @return {Function|null} experience handler
 */
export function getExperienceHandler(experienceId) {
	return resolve('experiences', experienceId);
}

/**
 * Resolve a canonical-to-Canvas prefill mapper.
 * @param {string} formId semantic form identifier
 * @return {Function|null} prefill mapper
 */
export function getPrefillMapper(formId) {
	return resolve('prefillMappers', formId);
}

/**
 * Resolve a command completion verifier.
 * @param {string} commandTarget command and semantic target identifier
 * @return {Function|null} completion verifier
 */
export function getCompletionVerifier(commandTarget) {
	return resolve('completionVerifiers', commandTarget);
}

/**
 * Resolve a runtime operation guard.
 * @param {string} capabilityId semantic capability identifier
 * @return {Function|null} operation guard
 */
export function getOperationGuard(capabilityId) {
	return resolve('operationGuards', capabilityId);
}

/**
 * Evaluate runtime operation availability without duplicating Canvas rules.
 * @param {string} capabilityId semantic capability identifier
 * @param {object} input resolved entity and mounted UI context
 * @return {Promise<object>} availability result
 */
export async function evaluateOperationAvailability(capabilityId, input = {}) {
	const guard = getOperationGuard(capabilityId);
	if (!guard) return {available: true, reason: null};
	const result = await guard(input);
	if (typeof result === 'boolean') {
		return {available: result, reason: null};
	}
	if (!result || typeof result.available !== 'boolean') {
		throw new Error(`invalid_operation_guard_result_${capabilityId}`);
	}
	return {
		available: result.available,
		reason: typeof result.reason === 'string' && result.reason
			? result.reason
			: null,
	};
}

/**
 * Convert mounted page context through installed resource plugins.
 * @param {object} context raw mounted page context
 * @return {object} public agent page context fields
 */
export function serializeResourcePageContext(context) {
	const serialized = {};
	indexes.contextSerializers.forEach(({value: serializer}) => {
		Object.assign(serialized, serializer(context));
	});
	return serialized;
}

/**
 * Check whether an installed plugin supports a capability.
 * @param {string} capabilityId capability identifier
 * @return {boolean} support status
 */
export function isCapabilitySupported(capabilityId) {
	return indexes.capabilities.has(capabilityId);
}

/**
 * Return the safe installed-plugin manifest.
 * @return {Array} plugin discovery records
 */
export function discoverResourcePlugins() {
	return Array.from(plugins.values())
		.map((plugin) => ({
			id: plugin.id,
			version: plugin.version,
			command_version: plugin.commandVersion || 1,
			resource: plugin.resource || null,
			operations: [...(plugin.operations || [])].sort(),
			capabilities: Object.keys(plugin.capabilities || {}).sort(),
			dependencies: plugin.dependencies || {},
		}))
		.sort((left, right) => left.id.localeCompare(right.id));
}
